'use client';

import { ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { FM_CONTEXT_MENU_SURFACE } from '@/components/ui/menu-surface';
import i18nextMvp from '@/lib/i18n';

const tMvp = (k: string): string => String(i18nextMvp.t(k));

export type UseMultiVideoPickerParams = {
  urls: string[];
  activeUrl?: string;
  thumbHeightPx: number;
  onSelectUrl: (url: string) => void;
  disabled?: boolean;
};

export type MultiVideoPickerState = ReturnType<typeof useMultiVideoPicker>;

export function useMultiVideoPicker(params: UseMultiVideoPickerParams) {
  const { urls, activeUrl, thumbHeightPx, onSelectUrl, disabled } = params;

  const safeUrls = useMemo(() => (Array.isArray(urls) ? urls.filter(Boolean) : []), [urls]);

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState<Record<string, true>>({});

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (safeUrls.length <= 1) setOpen(false);
  }, [safeUrls.length]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: Event) => {
      try {
        const target = e.target as Node | null;
        if (!target) return;
        if (
          target instanceof HTMLElement &&
          (target.closest('[data-slot="context-menu-content"]') ||
            target.closest('[data-slot="context-menu-sub-content"]'))
        ) {
          return;
        }
        if (triggerRef.current && triggerRef.current.contains(target)) return;
        if (menuRef.current && menuRef.current.contains(target)) return;
        setOpen(false);
      } catch {}
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const thumbStyle = useMemo(
    () => ({ aspectRatio: '16/9', height: `${thumbHeightPx}px` }) as const,
    [thumbHeightPx],
  );

  const isActive = useCallback(
    (url: string) => Boolean(activeUrl && url === activeUrl),
    [activeUrl],
  );

  const onThumbLoaded = useCallback((url: string) => {
    setThumbLoaded((prev) => (prev[url] ? prev : { ...prev, [url]: true }));
  }, []);

  const selectUrl = useCallback(
    (url: string) => {
      if (disabled) return;
      onSelectUrl(url);
    },
    [disabled, onSelectUrl],
  );

  return {
    urls: safeUrls,
    activeUrl,
    thumbHeightPx,
    open,
    setOpen,
    focused,
    setFocused,
    disabled: Boolean(disabled),
    triggerRef,
    menuRef,
    isActive,
    isThumbLoaded: useCallback(
      (url: string) => Boolean(thumbLoaded[url]),
      [thumbLoaded],
    ),
    selectUrl,
    onThumbLoaded,
    thumbStyle,
  };
}

export function MultiVideoTrigger(props: {
  state: MultiVideoPickerState;
  ariaLabel?: string;
  widthPx?: number;
  className?: string;
  onTriggerClick?: () => void;
}) {
  const { state, ariaLabel, widthPx = 100, className, onTriggerClick } = props;
  if (state.urls.length <= 1) return null;

  return (
    <button
      ref={state.triggerRef}
      type="button"
      className={
        `absolute top-3 right-3 z-20 inline-flex items-center justify-center gap-2 aspect-[16/9] px-3 rounded-2xl border border-white/10 bg-zinc-950/25 backdrop-blur-xl text-[14px] font-semibold tracking-tight text-white/90 shadow-xl shadow-black/20 hover:bg-zinc-950/30 active:scale-[0.98] transition ` +
        `${state.disabled ? 'opacity-50 pointer-events-none' : ''} ` +
        `${className ?? ''}`
      }
      style={{ width: `${widthPx}px` }}
      aria-label={ariaLabel ?? 'Videos'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          onTriggerClick?.();
        } catch {}
        state.setOpen(!state.open);
      }}
      onFocus={() => state.setFocused(true)}
      onBlur={() => state.setFocused(false)}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <span>+{Math.max(1, state.urls.length - 1)}</span>
      <ChevronDownIcon
        className={`h-5 w-5 opacity-80 transition-transform duration-200 ${
          (state.focused || state.open) ? '-rotate-90' : 'rotate-0'
        }`}
      />
    </button>
  );
}

export function MultiVideoMenuPanel(props: {
  state: MultiVideoPickerState;
  onCreateNodeFromThumb?: (url: string, index: number) => void;
  createNodeLabel?: string;
  className?: string;
}) {
  const { state, className, onCreateNodeFromThumb, createNodeLabel } = props;
  if (!state.open || state.urls.length <= 1) return null;

  return (
    <div
      ref={state.menuRef}
      className={
        `absolute right-0 top-1/2 -translate-y-1/2 translate-x-[calc(100%+0.75rem)] z-40 grid w-max grid-cols-[repeat(4,max-content)] gap-2 ` +
        `${className ?? ''}`
      }
    >
      {state.urls.map((u, i) => {
        const active = state.isActive(u);
        return (
          <ContextMenu key={`${u}-${i}`}>
            <ContextMenuTrigger asChild>
              <button
                type="button"
                className="relative overflow-hidden rounded-xl bg-zinc-900 focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/60 focus-visible:outline-offset-[-2px]"
                style={state.thumbStyle}
                title={`Video ${i + 1}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  state.selectUrl(u);
                }}
                onMouseDown={(e) => {
                  if ((e as any)?.button === 2) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  if ((e as any)?.button === 2) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {!state.isThumbLoaded(u) ? (
                  <>
                    <div className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-white/5 animate-pulse" />
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                      <Loader2Icon className="h-4 w-4 text-white/60 animate-spin" />
                    </div>
                  </>
                ) : null}
                <video
                  src={u}
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover relative z-10"
                  onLoadedData={() => state.onThumbLoaded(u)}
                  onError={() => state.onThumbLoaded(u)}
                />
                <div className="pointer-events-none absolute top-0 right-0 z-20 h-7 w-7 bg-black/35 text-white text-[11px] font-semibold backdrop-blur rounded-bl-full flex items-center justify-center leading-none">
                  {i + 1}
                </div>
                {active ? (
                  <div className="pointer-events-none absolute inset-0 z-30 rounded-xl ring-2 ring-inset ring-primary" />
                ) : null}
              </button>
            </ContextMenuTrigger>

            {onCreateNodeFromThumb ? (
              <ContextMenuContent
                className={FM_CONTEXT_MENU_SURFACE}
                onContextMenu={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <ContextMenuItem
                  onSelect={() => {
                    onCreateNodeFromThumb(u, i);
                    state.setOpen(false);
                  }}
                >
                  {createNodeLabel ?? tMvp('新建节点')}
                </ContextMenuItem>
              </ContextMenuContent>
            ) : null}
          </ContextMenu>
        );
      })}
    </div>
  );
}
